import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import { updateLocationResult } from '@alltrails/explore-service/redux/exploreServiceSlice';
import { bottomRightLatParamKey, bottomRightLngParamKey, topLeftLatParamKey, topLeftLngParamKey } from '@alltrails/filters/utils/exploreSearchParams';
import useMap from '@alltrails/maps/hooks/useMap';
import { explorePath } from '@/utils/constants';
import { useAppDispatch, useAppSelector } from '@/utils/redux';

/**
 * Are you tired of losing your map location when navigating back to the explore root? Then this is the hook for you!
 * This maintains all existing search params when routing back to `/explore` to support filters persisting across routes.
 *
 * @param {boolean} [persistMapLocation=false] - if true, keeps the map bounds when routing back to explore root. Otherwise, `returningBounds` will be used from redux (or no bounds if they don't exist).
 * @returns {Function} - A function to route you back to `/explore` with the appropriate parameters.
 */
export function useReturnToExploreRoot(persistMapLocation: boolean = false) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const searchParams = useSearchParams();
  const map = useMap();
  const languageRegionCode = useLanguageRegionCode();

  const { returningBounds } = useAppSelector(state => ({
    returningBounds: state.explore.returningBounds
  }));

  const returnToExploreRoot = useCallback(() => {
    dispatch(updateLocationResult(undefined));
    if (persistMapLocation) {
      const mapBounds = map?.getBounds();
      if (!mapBounds) {
        router.push(wrapUrlSafe(`${explorePath}?${searchParams}`, languageRegionCode));
        return;
      }

      const boundingBoxParams = new URLSearchParams();
      boundingBoxParams.set(bottomRightLatParamKey, String(mapBounds._sw.lat));
      boundingBoxParams.set(bottomRightLngParamKey, String(mapBounds._ne.lng));
      boundingBoxParams.set(topLeftLatParamKey, String(mapBounds._ne.lat));
      boundingBoxParams.set(topLeftLngParamKey, String(mapBounds._sw.lng));

      const allParamsStr = decodeURIComponent(`${boundingBoxParams}&${searchParams}`);
      router.push(`${wrapUrlSafe(explorePath, languageRegionCode)}?${allParamsStr}`);
    } else if (returningBounds) {
      const boundingBoxParams = new URLSearchParams();
      boundingBoxParams.set(bottomRightLatParamKey, String(returningBounds.latitudeBottomRight));
      boundingBoxParams.set(bottomRightLngParamKey, String(returningBounds.longitudeBottomRight));
      boundingBoxParams.set(topLeftLatParamKey, String(returningBounds.latitudeTopLeft));
      boundingBoxParams.set(topLeftLngParamKey, String(returningBounds.longitudeTopLeft));

      const allParamsStr = decodeURIComponent(`${boundingBoxParams}&${searchParams}`);
      router.push(`${wrapUrlSafe(explorePath, languageRegionCode)}?${allParamsStr}`);
    } else {
      router.push(wrapUrlSafe(`${explorePath}?${searchParams}`, languageRegionCode));
    }
  }, [dispatch, languageRegionCode, map, persistMapLocation, returningBounds, router, searchParams]);

  return returnToExploreRoot;
}
